import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import StatsCard from '../../../components/common/StatsCard'
import { ShoppingBagIcon, EyeIcon, ClockIcon, HeartIcon } from '@heroicons/react/24/outline'
import QuickAction from '../../../components/retailer/QuickAction'
import { getQuickActions } from '../../../constants/retailer'
import { handleGetRequest } from '../../../utils/api-handler.util'
import { useQuery } from '@tanstack/react-query'
import PageTitle from '../../../components/common/PageTitle'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import USER_TYPE from '../../../constants/userType'
import DisplayForUserType from '../../../components/auth/DisplayForUserType'

const ShopHomePage = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()

  const { data: statsResponse, isLoading } = useQuery({
    queryKey: QueryKeys.retailerShopStats(currentUser?.id),
    queryFn: () => handleGetRequest(`/${currentUser?.userType}s/my-stats`),
    enabled: !!currentUser?.id,
  })

  const stats = statsResponse?.data || {}

  const statsData = [
    {
      title: 'Total Products',
      value: stats.totalProducts ?? (isLoading ? '...' : 0),
      icon: ShoppingBagIcon,
      iconColor: 'text-secondary-600',
      iconBgColor: 'bg-orange-50',
      trend: 'up',
      onClick: () => navigate(`/my-products`),
      userTypes: [USER_TYPE.retailer, USER_TYPE.wholeseller],
    },
    {
      title: 'Shop Views',
      value: stats.totalShopViews ?? (isLoading ? '...' : 0),
      icon: EyeIcon,
      iconColor: 'text-blue-600',
      iconBgColor: 'bg-blue-50',
      trend: 'up',
      userTypes: [USER_TYPE.retailer, USER_TYPE.wholeseller],
    },
    {
      title: 'Favorites',
      value: stats.totalFavorites ?? (isLoading ? '...' : 0),
      icon: HeartIcon,
      iconColor: 'text-red-600',
      iconBgColor: 'bg-red-50',
      userTypes: [USER_TYPE.retailer],
    },
    {
      title: 'Bid Requests',
      value: stats.totalBidRequests ?? (isLoading ? '...' : 0),
      icon: ClockIcon,
      iconColor: 'text-yellow-600',
      iconBgColor: 'bg-yellow-50',
      onClick: () => navigate(`/${currentUser?.userType}/product-requests`),
      userTypes: [USER_TYPE.retailer],
    },
  ]

  return (
    <div className="main-container">
      <div className="mb-6">
        <PageTitle>Dashboard Overview</PageTitle>
        <p className="text-gray-600 mt-1">
          Monitor your shop's performance and manage your business
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2  gap-4 mb-8">
        {statsData.map((stat, index) => (
          <DisplayForUserType key={index} userTypes={stat.userTypes}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconColor={stat.iconColor}
              iconBgColor={stat.iconBgColor}
              trend={stat.trend}
              trendValue={stat.trendValue}
              onClick={stat.onClick}
            />
          </DisplayForUserType>
        ))}
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {getQuickActions(currentUser?.userType, navigate).map((action, index) => (
            <QuickAction
              key={index}
              title={action.title}
              description={action.description}
              bgColor={action.bgColor}
              textColor={action.textColor}
              border={action.border}
              onClick={action.action}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ShopHomePage
